import { useEffect } from "react";

/**
 * Sets <title>, <meta name="description">, canonical link, and optional JSON-LD
 * for the current page. Cleans up on unmount/route change.
 *
 * @param {object} opts
 * @param {string} opts.title - page title (will be suffixed " | Lifespan Wealth Partners" if not already)
 * @param {string} opts.description - meta description
 * @param {object|object[]} [opts.jsonLd] - structured data object(s)
 * @param {string} [opts.canonical] - canonical path (defaults to current location)
 */
export function usePageSEO({ title, description, jsonLd, canonical } = {}) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const previousTitle = document.title;

    const fullTitle = title && !title.includes("Lifespan Wealth Partners")
      ? `${title} | Lifespan Wealth Partners`
      : title;
    if (fullTitle) document.title = fullTitle;

    // Description
    let descTag = document.querySelector('meta[name="description"]');
    const previousDesc = descTag ? descTag.getAttribute("content") : null;
    if (description) {
      if (!descTag) {
        descTag = document.createElement("meta");
        descTag.setAttribute("name", "description");
        document.head.appendChild(descTag);
      }
      descTag.setAttribute("content", description);
    }

    // Canonical
    let linkTag = document.querySelector('link[rel="canonical"]');
    const previousCanonical = linkTag ? linkTag.getAttribute("href") : null;
    if (canonical || typeof window !== "undefined") {
      if (!linkTag) {
        linkTag = document.createElement("link");
        linkTag.setAttribute("rel", "canonical");
        document.head.appendChild(linkTag);
      }
      linkTag.setAttribute("href", canonical || window.location.href);
    }

    // JSON-LD
    const jsonLdNodes = [];
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((obj, i) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.seo = "page-jsonld";
        script.text = JSON.stringify(obj);
        document.head.appendChild(script);
        jsonLdNodes.push(script);
      });
    }

    return () => {
      document.title = previousTitle;
      if (previousDesc !== null && descTag) descTag.setAttribute("content", previousDesc);
      if (previousCanonical !== null && linkTag) linkTag.setAttribute("href", previousCanonical);
      jsonLdNodes.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, JSON.stringify(jsonLd), canonical]);
}

export default usePageSEO;
